export default customElements.define('w-chip', class Chip extends wealthify.CustomElementBaseClass {
    constructor () {
        const selector = 'c-chip';
        const config = {
            styleSelector: selector,
            templateHTML: `
                <div class="${selector}__outer-container" data-root>
                    <label class="${selector}__container" data-label>
                        <w-icon data-iconleft></w-icon>
                        <div class="${selector}__content" data-labelcontent></div>
                        <w-icon data-iconright></w-icon>
                    </label>
                    <button class="${selector}__container ${selector}--button u-display__none" type="button" data-button>
                        <w-icon data-iconleft></w-icon>
                        <div class="${selector}__content" data-buttoncontent></div>
                        <w-icon data-iconright></w-icon>
                    </button>
                </div>`,
            cacheDom: [ 'data-label', 'data-labelcontent', 'data-button', 'data-buttoncontent', 'data-iconleft', 'data-iconright' ]
        };

        super(config);

        this.onConnectedCallback = () =>{
            this.classList.add(`${this.styleSelector}`);
        };
    }

    static get validContexts () {
        return [ 'button', 'label', 'submit' ];
    }

    static get validTypes () {
        return [ 'default', 'highlight', 'primary', 'primary-warning', 'secondary', 'tertiary'];
    }

    static get allTypes () {
        return [ ...this.validTypes, 'primary-dark', 'secondary-dark' ];
    }

    static get observedProps () {
        return [
            new wealthify.Prop('hovered', {
                default: false,
                type: wealthify.propTypes.Boolean()
            }),
            
            new wealthify.Prop('context', {
                default: 'label',
                type: wealthify.propTypes.ArrayItem(this.validContexts)
            }),

            new wealthify.Prop('label', {
                default: 'Label',
                required: true
            }),

            new wealthify.Prop('onclickprop', {type: wealthify.propTypes.Function()}),

            new wealthify.Prop('submitform'),

            new wealthify.Prop('submitformv2'),

            new wealthify.Prop('type', {
                default: 'primary',
                required: true,
                type: wealthify.propTypes.ArrayItem(this.validTypes)
            }),

            wealthify.props.AriaHidden(),
            wealthify.props.DarkMode(),
            wealthify.props.Disabled(),
            wealthify.props.Icon({name: 'iconleft'}),
            wealthify.props.Icon({name: 'iconright'}),
        ];
    }

    get propCallbacks() {
        return {
            ariahidden: (value) => {
                if (value) {
                    this.dom.root.setAttribute('aria-hidden', true);
                    this.dom.button.setAttribute('tabindex', -1);
                } else {
                    this.dom.root.removeAttribute('aria-hidden');
                    this.dom.button.removeAttribute('tabindex');
                }
            },

            context: (value) => {
                const isButton = value === 'button' || value === 'submit';
                const buttonClassListAction = isButton ? 'remove' : 'add';
                const labelClassListAction = isButton ? 'add' : 'remove';

                if (value === 'submit') {
                    console.group('Deprecated props');
                    console.warn('The submit value being passed to the context prop is deprecated on the Chip component. Please use submitformv2.');
                    console.groupEnd();

                    this.dom.button.setAttribute('type', 'submit');
                }

                this.dom.button.classList[buttonClassListAction]('u-display__none');
                this.dom.label.classList[labelClassListAction]('u-display__none');
            },

            darkmode: () => this._switchType(),

            disabledprop: (value) => {
                const buttonClassListAction = value ? 'add' : 'remove';

                this.dom.label.classList[buttonClassListAction](`${this.styleSelector}--disabled`);
                this.dom.button.classList[buttonClassListAction](`${this.styleSelector}--disabled`);
                this.dom.button.disabled = value;
            },

            hovered: (value) => {
                const classListAction = value ? 'add' : 'remove';

                this.dom.label.classList[classListAction](`${this.styleSelector}--hovered`);
                this.dom.button.classList[classListAction](`${this.styleSelector}--hovered`);
            },

            iconleft: () => this.#updateIcon(this.iconleft, this.dom.iconleft, 'u-margin__left-mobile--spacing-s'),
            iconright: () => this.#updateIcon(this.iconright, this.dom.iconright, 'u-margin__right-mobile--spacing-s'),

            label: (value) => {
                this.dom.labelcontent.innerHTML = value;
                this.dom.buttoncontent.innerHTML = value;
            },

            onclickprop: (value) => this.dom.button.onclick = value,

            submitform: () => {
                console.group('Deprecated props');
                console.warn('The submitform prop is deprecated on the Chip component. Please use submitformv2.');
                console.groupEnd();

                this.dom.button.setAttribute('form', this.submitform);
            },

            submitformv2: () => {
                let type = 'button',
                    onclickprop = this.onclickprop;

                if (this.submitformv2 !== '') {

                    type = 'submit';
                    this.dom.root.setAttribute('form', this.submitformv2);

                    onclickprop = () => {

                        const form = document.querySelector(`#${this.submitformv2}`);
                        const formNotValid = !form.checkValidity();

                        if (formNotValid) {
                            return this._fireValidationAndFocusFirstInvalid(form);
                        }

                        form.submit();
                    };
                }

                this.dom.button.setAttribute('type', type);
                this.onclickprop = onclickprop;
            },

            type: () => this._switchType(),
        };
    }

    #updateIcon (value, elements, utilityClass) {
        const classListAction = value ? 'add' : 'remove';

        elements.forEach((element) => element.type = value);

        this.dom.buttoncontent.classList[classListAction](utilityClass);
        this.dom.labelcontent.classList[classListAction](utilityClass);
    }

    _switchType () {
        const type = this.darkmode ? `${this.type}-dark` : this.type;

        this.constructor.allTypes.forEach((type) => {
            this.classList.remove(`${this.styleSelector}--${type}`);
            this.classList.remove(`${this.styleSelector}--${type}-dark`);
        });
        this.classList.add(`${this.styleSelector}--${type}`);
    }

    _fireValidationAndFocusFirstInvalid (form) {
        const invalidFields = Array.prototype.filter.call(form.elements, (element) => element.tagName.toLowerCase() !== 'button' && !element.checkValidity());

        let firstInvalidFieldgroup;

        invalidFields.forEach((field, index) => {
            const fieldComponent = this._getWrappingComponent(field);
            
            fieldComponent.fireValidation();

            if (index === 0) {
                firstInvalidFieldgroup = this._getWrappingComponent(fieldComponent);
            }
        });

        const scrollLocation = window.scrollY + firstInvalidFieldgroup.getBoundingClientRect().top;

        invalidFields[0].focus();
        window.scrollTo({top: scrollLocation});
    }

    _getWrappingComponent (element) {
        return element.getWrappingComponent ? element.getWrappingComponent() : element;
    }
});